// components/dashboard/InsightsTempoReal.jsx
import React from 'react';
import {
  TrendingUp, BarChart3, CheckCircle, Calendar, Clock, Building, DollarSign, FileText
} from 'lucide-react';

const InsightsTempoReal = ({ insightsTempoReal, atualizacaoAutomatica, loading = false }) => {
  // Garantir que insightsTempoReal seja sempre um array
  const insights = Array.isArray(insightsTempoReal) ? insightsTempoReal : [];
  
  const coresConfig = {
    blue: { border: 'border-sky-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-sky-300' },
    green: { border: 'border-emerald-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-emerald-300' },
    purple: { border: 'border-purple-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-purple-300' },
    orange: { border: 'border-orange-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-orange-300' },
    indigo: { border: 'border-indigo-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-indigo-300' },
    pink: { border: 'border-pink-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-pink-300' },
    red: { border: 'border-rose-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-rose-300' },
    emerald: { border: 'border-emerald-400/60', bg: 'bg-[color:var(--surface)]', text: 'text-emerald-300' }
  };

  const IconeComponente = (nome) => {
    switch (nome) {
      case 'trending-up': return TrendingUp;
      case 'trending-down': return TrendingUp; // com rotação
      case 'bar-chart': return BarChart3;
      case 'check-circle': return CheckCircle;
      case 'calendar': return Calendar;
      case 'clock': return Clock;
      case 'building': return Building;
      case 'dollar-sign': return DollarSign;
      case 'file-text': return FileText;
      default: return TrendingUp;
    }
  };

  return (
    <div className="p-4 h-full min-h-[320px] md:min-h-[420px] flex flex-col rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur transition-colors duration-200 hover:bg-[color:var(--surface-muted)]">
      <div className="flex items-center mb-3 flex-shrink-0">
        <div className="flex items-center">
          <div className={`${atualizacaoAutomatica ? 'bg-emerald-500/20' : 'bg-amber-500/20'} p-2 rounded-full mr-3 transition-colors duration-300`}>
            <CheckCircle className={`h-5 w-5 ${atualizacaoAutomatica ? 'text-emerald-300 animate-pulse' : 'text-amber-300'}`} />
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-[0.35em] text-[color:var(--text-muted)] uppercase">Insights</div>
            <h2 className="text-base font-semibold text-[color:var(--text-primary)] mt-1 tracking-tight">Leituras Inteligentes</h2>
          </div>
        </div>
      </div>

      {/* Layout responsivo: Lista compacta no mobile, Grid no desktop */}
      <div className="flex-1 overflow-hidden">
        {/* Mobile: Lista compacta */}
        <div className="block md:hidden">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((index) => (
                <div key={index} className="p-3 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] animate-pulse">
                  <div className="h-3 w-24 bg-[color:var(--surface-muted)] rounded mb-2"></div>
                  <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
                </div>
              ))}
            </div>
          ) : insights.length > 0 ? (
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
              {insights.map((insight, index) => {
                if (!insight || typeof insight !== 'object') return null;

                const cores = coresConfig[insight.cor] || coresConfig.blue;
                const Icone = IconeComponente(insight.icone);

                const valorFormatado = (() => {
                  try {
                    if (insight.descricao) {
                      return String(insight.descricao);
                    }
                    if (insight.formato === 'titulos' && insight.valor) {
                      return `${Number(insight.valor).toLocaleString('pt-BR')} títulos`;
                    }
                    if (insight.formato === 'valor' && insight.valor) {
                      return `R$ ${Number(insight.valor).toLocaleString('pt-BR')}`;
                    }
                    if (insight.formato === 'percentual' && insight.valor) {
                      return `${insight.valor}%`;
                    }
                    if (insight.formato === 'texto') {
                      return String(insight.valorTexto || insight.descricao || 'Insight');
                    }
                    return insight.valor ? `R$ ${Number(insight.valor).toLocaleString('pt-BR')}` : String(insight.valorTexto || insight.titulo || 'Insight');
                  } catch (error) {
                    return String(insight.descricao || insight.valorTexto || insight.titulo || 'Insight');
                  }
                })();

                return (
                  <div key={insight.id || index} className={`p-3 rounded-lg border-l-4 ${cores.border} ${cores.bg} transition-colors duration-200 hover:bg-[color:var(--surface-muted)]`}>
                    <div className="flex items-start">
                      <Icone className={`h-4 w-4 mr-3 mt-0.5 ${cores.text} ${insight.icone === 'trending-down' ? 'rotate-180' : ''}`} />
                      <div className="flex-1">
                        <h3 className="font-semibold text-[color:var(--text-primary)] text-sm">
                          {String(insight.titulo || `Insight ${index + 1}`)}
                        </h3>
                        <p className="text-xs text-[color:var(--text-muted)] mt-1 leading-relaxed">
                          {valorFormatado}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-[color:var(--text-muted)]">
              <div className="text-4xl mb-2">📊</div>
              <p className="text-sm">Nenhum insight disponível no momento</p>
              <p className="text-xs text-[color:var(--text-muted)] mt-1">Os insights serão carregados automaticamente</p>
            </div>
          )}
        </div>

        {/* Tablet: Grid 2 colunas */}
        <div className="hidden md:block lg:hidden h-full">
          <div className="grid grid-cols-2 gap-3 h-full overflow-y-auto">
            {loading ? (
              [1, 2, 3, 4].map((index) => (
                <div key={index} className="p-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] animate-pulse">
                  <div className="h-3 w-20 bg-[color:var(--surface-muted)] rounded mb-2"></div>
                  <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
                </div>
              ))
            ) : insights.length > 0 ? insights.map((insight, index) => {
              // Verificar se insight é válido
              if (!insight || typeof insight !== 'object') {
                console.warn('⚠️ Insight inválido:', insight);
                return null;
              }

              const cores = coresConfig[insight.cor] || coresConfig.blue;
              const Icone = IconeComponente(insight.icone);
              
              const valorFormatado = (() => {
                try {
                  if (insight.descricao) {
                    return String(insight.descricao);
                  }
                  
                  if (insight.formato === 'titulos' && insight.valor) {
                    return `${Number(insight.valor).toLocaleString('pt-BR')} títulos`;
                  }
                  if (insight.formato === 'valor' && insight.valor) {
                    return `R$ ${Number(insight.valor).toLocaleString('pt-BR')}`;
                  }
                  if (insight.formato === 'percentual' && insight.valor) {
                    return `${insight.valor}%`;
                  }
                  if (insight.formato === 'texto') {
                    return String(insight.valorTexto || insight.descricao || 'Insight');
                  }
                  
                  return insight.valor ? `R$ ${Number(insight.valor).toLocaleString('pt-BR')}` : String(insight.valorTexto || insight.titulo || 'Insight');
                } catch (error) {
                  console.error('❌ Erro ao formatar valor:', error);
                  return String(insight.descricao || insight.valorTexto || insight.titulo || 'Insight');
                }
              })();

              return (
                <div key={insight.id || index} className={`p-2 rounded-lg border-l-4 ${cores.border} ${cores.bg} border border-[color:var(--border)] shadow-sm min-h-[96px] transition-colors duration-200 hover:bg-[color:var(--surface-muted)]`}>
                  <div className="flex items-start">
                    <Icone className={`h-4 w-4 mr-2 mt-0.5 ${cores.text} ${insight.icone === 'trending-down' ? 'rotate-180' : ''}`} />
                    <div className="flex-1">
                      <h3 className="font-semibold text-[color:var(--text-primary)] text-xs truncate">
                        {String(insight.titulo || `Insight ${index + 1}`)}
                      </h3>
                      <p className="text-xs text-[color:var(--text-muted)] mt-0.5 leading-tight">
                        {valorFormatado}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="col-span-2 text-center py-8 text-[color:var(--text-muted)]">
                <div className="text-4xl mb-2">📊</div>
                <p className="text-sm">Nenhum insight disponível no momento</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">Os insights serão carregados automaticamente</p>
              </div>
            )}
          </div>
        </div>

        {/* Desktop: Grid 3 colunas */}
        <div className="hidden lg:block h-full">
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 h-full overflow-y-auto">
        {loading ? (
          [1, 2, 3, 4, 5, 6].map((index) => (
            <div key={index} className="p-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] animate-pulse">
              <div className="h-3 w-20 bg-[color:var(--surface-muted)] rounded mb-2"></div>
              <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
            </div>
          ))
        ) : insights.length > 0 ? insights.map((insight, index) => {
          // Verificar se insight é válido
          if (!insight || typeof insight !== 'object') {
            console.warn('⚠️ Insight inválido:', insight);
            return null;
          }

          const cores = coresConfig[insight.cor] || coresConfig.blue;
          const Icone = IconeComponente(insight.icone);
          
          // Lógica melhorada para formatar valor considerando diferentes estruturas de dados
          const valorFormatado = (() => {
            try {
              // Se tem descrição (formato da API), usar como valor principal
              if (insight.descricao) {
                return String(insight.descricao);
              }
              
              // Formatação por tipo específico
              if (insight.formato === 'titulos' && insight.valor) {
                return `${Number(insight.valor).toLocaleString('pt-BR')} títulos`;
              }
              if (insight.formato === 'valor' && insight.valor) {
                return `R$ ${Number(insight.valor).toLocaleString('pt-BR')}`;
              }
              if (insight.formato === 'percentual' && insight.valor) {
                return `${insight.valor}%`;
              }
              if (insight.formato === 'texto') {
                return String(insight.valorTexto || insight.descricao || 'Insight');
              }
              
              // Fallback
              return insight.valor ? `R$ ${Number(insight.valor).toLocaleString('pt-BR')}` : String(insight.valorTexto || insight.titulo || 'Insight');
            } catch (error) {
              console.error('❌ Erro ao formatar valor:', error);
              return String(insight.descricao || insight.valorTexto || insight.titulo || 'Insight');
            }
          })();

          return (
            <div key={insight.id || index} className={`p-2 rounded-lg border-l-4 ${cores.border} ${cores.bg} border border-[color:var(--border)] shadow-sm flex flex-col justify-start min-h-[96px] transition-colors duration-200 hover:bg-[color:var(--surface-muted)]`}>
              <div className="flex items-start flex-1">
                <Icone className={`h-4 w-4 mr-2 mt-0.5 ${cores.text} ${insight.icone === 'trending-down' ? 'rotate-180' : ''}`} />
                <div className="flex-1">
                  <h3 className="font-semibold text-[color:var(--text-primary)] text-xs truncate">
                    {String(insight.titulo || `Insight ${index + 1}`)}
                  </h3>
                  <p className="text-xs text-[color:var(--text-muted)] mt-0.5 leading-tight">
                    {valorFormatado}
                  </p>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="col-span-3 text-center py-8 text-[color:var(--text-muted)]">
            <div className="text-4xl mb-2">📊</div>
            <p className="text-sm">Nenhum insight disponível no momento</p>
            <p className="text-xs text-[color:var(--text-muted)] mt-1">Os insights serão carregados automaticamente</p>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightsTempoReal;


